import React from 'react';
import { useReservationsContext } from '../../context/ReservationsContext';
import { useProductStatus, EffectiveStatus } from '../../hooks/useProductStatus';
import {
    CardContainer,
    ImageContainer,
    ProductImage,
    GalleryIndicator,
    ProductInfo,
    ProductName,
    ProductPrice,
    ProductDescription,
    ReserveButton,
    StatusOverlay,
    StatusBadge
} from './ProductCard.styled';

// Helper para normalizar imagen (soporta string u objeto { src, zoomable })
const getImageSrc = (img) => {
    if (typeof img === 'string') return img;
    return img.src;
};

const ProductCard = ({ product, onImageClick, onReserveClick }) => {
    const { loading } = useReservationsContext();
    const { getEffectiveStatus } = useProductStatus();

    const { effectiveStatus, timeRemaining, canReserve } = getEffectiveStatus(product);

    const mainImage = getImageSrc(product.images[0]);
    const hasGallery = product.images.length > 1;

    const handleImageClick = (e) => {
        e.stopPropagation();
        onImageClick();
    };

    const handleReserveClick = (e) => {
        e.stopPropagation();
        if (canReserve && !loading) {
            onReserveClick();
        }
    };

    const formatTimeRemaining = () => {
        if (!timeRemaining) return '';
        const { hours, minutes } = timeRemaining;
        if (hours > 0) {
            return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
    };

    const getButtonText = () => {
        if (loading) return 'Cargando...';

        switch (effectiveStatus) {
            case EffectiveStatus.SOLD:
                return 'Vendido';
            case EffectiveStatus.RESERVED:
                return `Reservado (${formatTimeRemaining()})`;
            default:
                return 'Reservar';
        }
    };

    const getOverlayContent = () => {
        switch (effectiveStatus) {
            case EffectiveStatus.SOLD:
                return { badge: 'VENDIDO', variant: 'sold' };
            case EffectiveStatus.RESERVED:
                return { badge: 'RESERVADO', variant: 'reserved' };
            default:
                return null;
        }
    };

    const overlay = getOverlayContent();
    const isDisabled = !canReserve || loading;

    return (
        <CardContainer $status={effectiveStatus}>
            <ImageContainer onClick={handleImageClick}>
                <ProductImage
                    src={`/img/${mainImage}`}
                    alt={product.name}
                    loading="lazy"
                />
                {hasGallery && (
                    <GalleryIndicator>
                        +{product.images.length - 1} fotos
                    </GalleryIndicator>
                )}
                {overlay && (
                    <StatusOverlay $variant={overlay.variant}>
                        <StatusBadge $variant={overlay.variant}>
                            {overlay.badge}
                        </StatusBadge>
                    </StatusOverlay>
                )}
            </ImageContainer>
            <ProductInfo>
                <ProductName>{product.name}</ProductName>
                <ProductPrice>{product.price.toFixed(2)} EUR</ProductPrice>
                <ProductDescription>{product.description}</ProductDescription>
                <ReserveButton
                    onClick={handleReserveClick}
                    disabled={isDisabled}
                    $status={effectiveStatus}
                >
                    {getButtonText()}
                </ReserveButton>
            </ProductInfo>
        </CardContainer>
    );
};

export default ProductCard;
